import { Button } from "../ui/button";
import { ArrowUp, ArrowDown, Edit2, Trash2, Briefcase, GraduationCap, Heart } from "lucide-react";
import { type TimelineEvent } from "./MilestoneEditForm";

interface MilestoneRowActionsProps {
  event: TimelineEvent;
  idx: number;
  totalCount: number;
  onEdit: (event: TimelineEvent) => void;
  onDelete: (id: string) => void;
  onMove: (index: number, direction: "up" | "down") => void;
}

function MilestoneRowActions({
  event,
  idx,
  totalCount,
  onEdit,
  onDelete,
  onMove,
}: MilestoneRowActionsProps) {
  return (
    <>
      <td className="p-4 text-center">
        <div className="flex items-center justify-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onMove(idx, "up")}
            disabled={idx === 0}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <ArrowUp className="h-4.5 w-4.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onMove(idx, "down")}
            disabled={idx === totalCount - 1}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <ArrowDown className="h-4.5 w-4.5" />
          </Button>
        </div>
      </td>
      <td className="p-4 text-center">
        <div className="flex items-center justify-center gap-2">
          <Button
            size="icon"
            variant="outline"
            onClick={() => onEdit(event)}
            className="h-8 w-8 border-border/60 hover:bg-brand-primary/10 hover:text-brand-primary text-muted-foreground"
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="outline"
            onClick={() => onDelete(event.id)}
            className="h-8 w-8 border-border/60 hover:bg-destructive/10 hover:text-destructive text-muted-foreground"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </>
  );
}

function getPlaceholderIcon(type: string) {
  if (type === "professional") {
    return <Briefcase className="h-5 w-5" />;
  }
  if (type === "academic") {
    return <GraduationCap className="h-5 w-5" />;
  }
  return <Heart className="h-5 w-5" />;
}

interface MilestoneRowProps {
  event: TimelineEvent;
  idx: number;
  totalCount: number;
  onEdit: (event: TimelineEvent) => void;
  onDelete: (id: string) => void;
  onMove: (index: number, direction: "up" | "down") => void;
}

function MilestoneRow({
  event,
  idx,
  totalCount,
  onEdit,
  onDelete,
  onMove,
}: MilestoneRowProps) {
  return (
    <tr className="hover:bg-muted/10 transition-colors">
      <td className="p-4 text-center font-mono font-bold text-muted-foreground">
        {idx + 1}
      </td>
      <td className="p-4 w-16">
        {event.logo_url ? (
          <img
            src={event.logo_url}
            alt={event.company_en}
            className="w-10 h-10 rounded-full object-cover border border-border/80"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-muted/30 border border-border/60 flex items-center justify-center text-muted-foreground">
            {getPlaceholderIcon(event.type)}
          </div>
        )}
      </td>
      <td className="p-4 max-w-xs space-y-1">
        <p className="font-mono text-xs font-bold text-foreground leading-snug">
          {event.year}
        </p>
        <p className="text-[11px] font-semibold text-brand-secondary">
          {event.duration_en || event.duration}
        </p>
        {event.duration_tr && event.duration_tr !== (event.duration_en || event.duration) && (
          <p className="text-[10px] text-muted-foreground italic leading-none">
            {event.duration_tr}
          </p>
        )}
      </td>
      <td className="p-4 max-w-xs space-y-1">
        <p className="font-semibold text-foreground leading-snug">
          {event.role_en}
        </p>
        {event.role_tr && event.role_tr !== event.role_en && (
          <p className="text-xs text-muted-foreground italic">
            {event.role_tr}
          </p>
        )}
      </td>
      <td className="p-4 max-w-xs space-y-1">
        <p className="text-xs font-semibold text-brand-secondary">
          {event.company_en}
        </p>
        {event.company_tr && event.company_tr !== event.company_en && (
          <p className="text-[11px] text-muted-foreground italic">
            {event.company_tr}
          </p>
        )}
      </td>
      <MilestoneRowActions
        event={event}
        idx={idx}
        totalCount={totalCount}
        onEdit={onEdit}
        onDelete={onDelete}
        onMove={onMove}
      />
    </tr>
  );
}

interface MilestoneListProps {
  events: TimelineEvent[];
  onMove: (index: number, direction: "up" | "down") => void;
  onEdit: (event: TimelineEvent) => void;
  onDelete: (id: string) => void;
}

export function MilestoneList({ events, onMove, onEdit, onDelete }: MilestoneListProps) {
  if (events.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed border-border/60 rounded-2xl bg-card/40">
        <p className="text-sm font-semibold text-muted-foreground">
          No milestones configured. Click the button above to add one.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-border/60 bg-card rounded-2xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse font-sans">
          <thead>
            <tr className="bg-muted/40 border-b border-border/60 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <th className="p-4 w-12 text-center">#</th>
              <th className="p-4 w-16">Logo</th>
              <th className="p-4">Year / Duration</th>
              <th className="p-4">Role (EN / TR)</th>
              <th className="p-4">Company (EN / TR)</th>
              <th className="p-4 w-28 text-center">Order</th>
              <th className="p-4 w-36 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40 text-sm">
            {events.map((event, idx) => (
              <MilestoneRow
                key={event.id}
                event={event}
                idx={idx}
                totalCount={events.length}
                onEdit={onEdit}
                onDelete={onDelete}
                onMove={onMove}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MilestoneList;

